import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Eye, Send, Search, DollarSign, CheckCircle, Clock, XCircle
} from 'lucide-react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ResponsiveTable } from '@/components/ui/ResponsiveTable';
import { useAuthStore } from '@/store/authStore';
import { invoiceService, type Invoice } from '@/services/invoiceService';
import { useGlobalToast } from '@/contexts/ToastContext';
import { FadeIn } from '@/components/ui/AnimatedComponents';
import { SkeletonCard } from '@/components/ui/SkeletonLoaders';

export function InvoicesPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { success, error } = useGlobalToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'unpaid' | 'paid' | 'cancelled'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalAmount: 0,
    paidAmount: 0,
    unpaidAmount: 0,
    cancelledAmount: 0
  });

  useEffect(() => {
    fetchInvoices();
  }, [user]);

  const fetchInvoices = async () => {
    if (!user?.id) return;
    
    try {
      setIsLoading(true);
      const data = await invoiceService.getProviderInvoices(user.id);
      setInvoices(data);

      // Calculate stats
      setStats({
        totalAmount: data.reduce((acc, curr) => acc + curr.amount, 0),
        paidAmount: data.filter(i => i.status === 'PAID').reduce((acc, curr) => acc + curr.amount, 0),
        unpaidAmount: data.filter(i => i.status === 'UNPAID').reduce((acc, curr) => acc + curr.amount, 0),
        cancelledAmount: data.filter(i => i.status === 'CANCELLED').reduce((acc, curr) => acc + curr.amount, 0),
      });
    } catch (err: any) {
      error('Unable to Load Invoices', err.message || 'Please check your connection and try again');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    try {
      await invoiceService.updateInvoiceStatus(invoiceId, 'PAID');
      success('Success', 'Invoice marked as paid');
      fetchInvoices();
    } catch (err: any) {
      error('Update Failed', err.message || 'Unable to update invoice status. Please try again');
    }
  };

  const handleResendInvoice = async (invoiceId: string) => {
    try {
      await invoiceService.resendInvoice(invoiceId);
      success('Success', 'Invoice resent to customer');
    } catch (err: any) {
      error('Send Failed', err.message || 'Unable to resend invoice. Please check your connection');
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesTab = activeTab === 'all' || invoice.status.toLowerCase() === activeTab;
    const matchesSearch = invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.jobTitle.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const getStatusVariant = (status: Invoice['status']) => {
    switch (status) {
      case 'PAID': return 'success';
      case 'UNPAID': return 'warning';
      case 'CANCELLED': return 'error';
      default: return 'pending';
    }
  };

  const getStatusIcon = (status: Invoice['status']) => {
    switch (status) {
      case 'PAID': return <CheckCircle className="w-4 h-4" />;
      case 'UNPAID': return <Clock className="w-4 h-4" />;
      case 'CANCELLED': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Invoices' }]} />

        <div className="page-header mt-4">
          <h1 className="page-title">Invoices</h1>
          <p className="page-description">Track payments and manage billing for completed jobs.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <FadeIn delay={0}>
            <Card className="p-6 border-l-4 border-l-primary-500 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-500">Total Invoiced</p>
                <DollarSign className="w-5 h-5 text-primary-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">€{stats.totalAmount.toFixed(2)}</h3>
              <p className="text-xs text-gray-500 mt-1">{invoices.length} invoices</p>
            </Card>
          </FadeIn>
          <FadeIn delay={50}>
            <Card className="p-6 border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-500">Paid</p>
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-emerald-600">€{stats.paidAmount.toFixed(2)}</h3>
              <p className="text-xs text-gray-500 mt-1">{invoices.filter(i => i.status === 'PAID').length} paid</p>
            </Card>
          </FadeIn>
          <FadeIn delay={100}>
            <Card className="p-6 border-l-4 border-l-warning-500 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-500">Unpaid</p>
                <Clock className="w-5 h-5 text-warning-600" />
              </div>
              <h3 className="text-2xl font-bold text-warning-600">€{stats.unpaidAmount.toFixed(2)}</h3>
              <p className="text-xs text-gray-500 mt-1">{invoices.filter(i => i.status === 'UNPAID').length} pending</p>
            </Card>
          </FadeIn>
          <FadeIn delay={150}>
            <Card className="p-6 border-l-4 border-l-gray-300 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-500">Cancelled</p>
                <XCircle className="w-5 h-5 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-600">€{stats.cancelledAmount.toFixed(2)}</h3>
              <p className="text-xs text-gray-500 mt-1">{invoices.filter(i => i.status === 'CANCELLED').length} cancelled</p>
            </Card>
          </FadeIn>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 w-full sm:w-auto">
              {['all', 'unpaid', 'paid', 'cancelled'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as any)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${activeTab === tab ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Table */}
          <ResponsiveTable
            data={filteredInvoices}
            columns={[
              {
                key: 'invoice',
                label: 'Invoice',
                mobileLabel: 'Invoice #',
                render: (invoice) => (
                  <div>
                    <div className="font-medium text-gray-900">{invoice.invoiceNumber}</div>
                    {invoice.paidDate && (
                      <div className="text-xs text-emerald-600 mt-0.5">
                        Paid: {new Date(invoice.paidDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )
              },
              {
                key: 'customer',
                label: 'Customer',
                render: (invoice) => (
                  <div>
                    <div className="font-medium text-gray-900">{invoice.customerName}</div>
                    <div className="text-xs text-gray-500">{invoice.customerEmail}</div>
                  </div>
                )
              },
              {
                key: 'job',
                label: 'Job',
                render: (invoice) => (
                  <div className="max-w-xs truncate text-gray-600">{invoice.jobTitle}</div>
                )
              },
              {
                key: 'amount',
                label: 'Amount',
                render: (invoice) => (
                  <span className="font-semibold text-gray-900">€{invoice.amount.toFixed(2)}</span>
                )
              },
              {
                key: 'status',
                label: 'Status',
                render: (invoice) => (
                  <Badge variant={getStatusVariant(invoice.status)} icon={getStatusIcon(invoice.status)}>
                    {invoice.status}
                  </Badge>
                )
              },
              {
                key: 'date',
                label: 'Issue Date',
                mobileLabel: 'Date',
                render: (invoice) => (
                  <span className="text-gray-600">
                    {new Date(invoice.issueDate).toLocaleDateString()}
                  </span>
                )
              },
              {
                key: 'actions',
                label: 'Actions',
                render: (invoice) => (
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors min-h-touch min-w-touch"
                      title="View Details"
                      aria-label="View invoice details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {invoice.status === 'UNPAID' && (
                      <>
                        <button
                          onClick={() => handleResendInvoice(invoice.id)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors min-h-touch min-w-touch"
                          title="Resend Email"
                          aria-label="Resend invoice email"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMarkAsPaid(invoice.id)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors min-h-touch min-w-touch"
                          title="Mark as Paid"
                          aria-label="Mark invoice as paid"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                )
              }
            ]}
            keyExtractor={(invoice) => invoice.id}
            isLoading={isLoading}
            emptyState={
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-900">No invoices found</p>
                <p className="text-sm text-gray-500 mt-1">
                  {activeTab === 'all'
                    ? 'Complete jobs to generate invoices automatically'
                    : `No ${activeTab} invoices`}
                </p>
              </div>
            }
          />
        </Card>
      </main>
    </div>
  );
}